import { useState } from 'react'
import classNames from 'classnames'
import * as Dialog from '@radix-ui/react-dialog'

import { Body, Button, Heading, TextArea } from '@foxino/components-web'
import { XClose } from '@foxino/icons-react'

import { BugReportMessageType } from '@app/data'
import { useTranslation } from '@app/locales'
import { useBugReporting } from '@app/modules/classroom/data/useBugReporting'

type Props = {
  onClose: () => void
}

export const BugReportDialog = ({ onClose }: Props) => {
  const { t } = useTranslation('common')
  const [bugType, setBugType] = useState<BugReportMessageType>(BugReportMessageType.BUG)
  const [message, setMessage] = useState('')

  const { actions, loading } = useBugReporting()

  const handleOnSubmit = async () => {
    const success = await actions.reportBug(bugType, message)

    if (success) {
      onClose()
    }
  }

  return (
    <Dialog.Root open={true}>
      <Dialog.Portal>
        <Dialog.Overlay onClick={onClose} className="fixed inset-0 z-[50] bg-black/50" />
        <Dialog.DialogTitle>{t('bugReport.title')}</Dialog.DialogTitle>
        <Dialog.Description>{t('bugReport.description')}</Dialog.Description>

        <Dialog.Content className="fixed bottom-20 left-20 z-[60] flex rounded-2xl bg-primary-1000 text-primary-200 shadow-2xl">
          <div className="relative z-[60] w-[380px] rounded-lg border border-primary-600 bg-primary-800 p-3">
            <Button
              size="xs"
              color="secondary"
              LeadingIcon={XClose}
              className="absolute right-3 top-3"
              onClick={onClose}
            />

            <Heading size="md" weight="semibold" className="mb-4 text-neutral-100">
              {t('bugReport.title')}
            </Heading>

            <Body size="md" weight="regular" className="mb-8 text-neutral-300">
              {t('bugReport.subtitle')}
            </Body>

            <div className="mb-6 flex items-center gap-2">
              <Button
                color="secondary"
                className="gap-2 bg-primary-700"
                onClick={() => setBugType(BugReportMessageType.BUG)}
              >
                <div
                  className={classNames(
                    'h-4 w-4 rounded-full border-primary-400 bg-white',
                    bugType === BugReportMessageType.BUG && 'border-4'
                  )}
                />
                {t('bugReport.bugType')}
              </Button>
              <Button
                color="secondary"
                className="gap-2 bg-primary-700"
                onClick={() => setBugType(BugReportMessageType.SUGGESTION)}
              >
                <div
                  className={classNames(
                    'h-4 w-4 rounded-full border-primary-400 bg-white',
                    bugType === BugReportMessageType.SUGGESTION && 'border-4'
                  )}
                />
                {t('bugReport.featureRequest')}
              </Button>
            </div>

            <TextArea
              autoFocus
              label={t('bugReport.description') + ':'}
              placeholder={t('bugReport.placeholder')}
              className="mb-4"
              textareaClassName="bg-primary-900"
              wrapperClassName="bg-primary-900"
              rows={8}
              maxLength={1000}
              onChange={(e) => setMessage(e.target.value)}
              value={message}
            />

            <div className="flex w-full justify-end">
              <Button
                loading={loading}
                disabled={loading || message.length === 0}
                onClick={handleOnSubmit}
                className=""
              >
                {t('submit')}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
